import { useCallback, useState } from "react";
import { Timestamp, doc, runTransaction } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";
import { toast } from "@/utils/toast";
import { type AssignmentHistoryEntry } from "@/hooks/useTenants";

export function useRemoveTenantMember(tenantId: string, tenantName: string) {
  const { t } = useTranslation(["admin"]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const removeMember = useCallback(
    async (email: string): Promise<boolean> => {
      setIsSubmitting(true);
      try {
        const tenantRef = doc(db, "tenants", tenantId);

        await runTransaction(db, async (tx) => {
          const snap = await tx.get(tenantRef);
          if (!snap.exists()) {
            throw new Error(`Tenant ${tenantId} does not exist`);
          }

          const data = snap.data();
          const currentEmails: string[] = data.active_user_emails ?? [];
          const currentHistory: AssignmentHistoryEntry[] =
            data.assignment_history ?? [];

          const normalizedEmail = email.toLowerCase();
          const updatedEmails = currentEmails.filter(
            (e) => e.toLowerCase() !== normalizedEmail,
          );

          if (updatedEmails.length === currentEmails.length) {
            return;
          }

          const removedEntry: AssignmentHistoryEntry = {
            action: "removed",
            email,
            date: Timestamp.now(),
          };

          tx.update(tenantRef, {
            active_user_emails: updatedEmails,
            assignment_history: [...currentHistory, removedEntry],
          });
        });

        toast.success(
          t("admin:tenants.detail.removeMemberDialog.success", {
            email,
            tenantName,
          }),
        );
        return true;
      } catch (err) {
        logError(err as Error);
        toast.error(
          t("admin:tenants.detail.removeMemberDialog.errorWriteFailed", {
            tenantName,
          }),
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [tenantId, tenantName, t],
  );

  return { removeMember, isSubmitting };
}
